import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// Admin / config / dashboards / tokens audit `data` schemas. See `registry.ts` for
// the audit-data leniency contract these follow.
// Shared empty payload.
export const emptyAdminDataSchema = z.object({});
// ===== Config =====
export const setSystemToggleDataSchema = z.object({
    toggleName: z.string().nullish(),
    value: z.boolean().nullish(),
});
export const setOrganisationConfigurationDataSchema = z.object({
    organisationId: z.string().nullish(),
});
export const setUserOrganisationConfigurationDataSchema = z.object({
    organisationId: z.string().nullish(),
    configuration: z.unknown().optional(), // the overrides as set, not modelled
});
// Shared by addApprovedEmailDomain / removeApprovedEmailDomain.
export const approvedEmailDomainDataSchema = z.object({
    pattern: z.string().nullish(),
});
export const updateHospitalMhlAutoRouteConfigDataSchema = z.object({
    locationId: z.string().nullish(),
    config: z.unknown().optional(), // opaque config payload
});
// ===== Admin =====
export const adminDeleteDataPermissionDataSchema = z.object({
    ticketNumber: z.string().nullish(), // free-text
});
export const scaryAdminDeletePatientMhaStatusesDataSchema = z.object({
    sinceDateTime: z.string().nullish(),
});
export const deleteEmptyAdmissionDataSchema = z.object({
    source: z.string().nullish(),
});
// ===== Dashboards / reporting =====
export const workReportDataSchema = z.object({
    params: z.unknown().optional(), // opaque report request body
    resultsCount: z.number().nullish(),
});
// Shared by nhsDashboardAccessed / supersetDashboardAccessed.
export const dashboardAccessedDataSchema = z.object({
    accessedAt: z.string().nullish(),
});
// Superset access also records whether the dashboard contained PID.
export const supersetDashboardAccessedDataSchema = dashboardAccessedDataSchema.extend({
    containsPid: z.boolean().nullish(),
});
export const supersetDashboardExportedDataSchema = z.object({
    exportedAt: z.string().nullish(),
    format: z.string().nullish(),
    containsPid: z.boolean().nullish(),
});
export const patientStateReportDataSchema = z.object({
    statuses: z.array(z.string()).nullish(),
    expiryLessThan: z.string().nullish(),
    trustIds: z.array(z.string()).nullish(),
    hospitalIds: z.array(z.string()).nullish(),
    patientSearchFilter: z.boolean().nullish(),
    numberOfResults: z.number().nullish(),
    reason: z.string().nullish(), // free-text
    reasonDescription: z.string().nullish(), // free-text
});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const adminAuditDataRegistry = {
    // Config.
    [AuditTrailEntryType.setSystemToggle]: setSystemToggleDataSchema,
    [AuditTrailEntryType.setOrganisationConfiguration]: setOrganisationConfigurationDataSchema,
    [AuditTrailEntryType.setUserOrganisationConfiguration]: setUserOrganisationConfigurationDataSchema,
    [AuditTrailEntryType.addApprovedEmailDomain]: approvedEmailDomainDataSchema,
    [AuditTrailEntryType.removeApprovedEmailDomain]: approvedEmailDomainDataSchema,
    [AuditTrailEntryType.updateHospitalMhlAutoRouteConfig]: updateHospitalMhlAutoRouteConfigDataSchema,
    // Admin.
    [AuditTrailEntryType.adminDeleteDataPermission]: adminDeleteDataPermissionDataSchema,
    [AuditTrailEntryType.bulkMigrateDataPermission]: emptyAdminDataSchema,
    [AuditTrailEntryType.adminCreateTermsAndConditions]: emptyAdminDataSchema,
    [AuditTrailEntryType.getGuestUserCode]: emptyAdminDataSchema,
    [AuditTrailEntryType.resetGuestUserLogins]: emptyAdminDataSchema,
    [AuditTrailEntryType.scaryAdminDeletePatientMhaStatuses]: scaryAdminDeletePatientMhaStatusesDataSchema,
    [AuditTrailEntryType.deleteEmptyAdmissions]: emptyAdminDataSchema,
    [AuditTrailEntryType.deleteEmptyAdmission]: deleteEmptyAdmissionDataSchema,
    // Single-use action tokens (empty payloads).
    [AuditTrailEntryType.createSingleUseActionToken]: emptyAdminDataSchema,
    [AuditTrailEntryType.revokeSingleUseActionToken]: emptyAdminDataSchema,
    [AuditTrailEntryType.useSingleUseActionToken]: emptyAdminDataSchema,
    // Dead-letter-queue admin jobs (empty payloads).
    [AuditTrailEntryType.bulkDeletesJobsFromDeadLetterQueue]: emptyAdminDataSchema,
    [AuditTrailEntryType.retryPatientJobsFromDeadLetterQueue]: emptyAdminDataSchema,
    [AuditTrailEntryType.deletePatientJobsFromDeadLetterQueue]: emptyAdminDataSchema,
    // Dashboards / reporting.
    [AuditTrailEntryType.workReport]: workReportDataSchema,
    [AuditTrailEntryType.patientStateReport]: patientStateReportDataSchema,
    [AuditTrailEntryType.nhsDashboardAccessed]: dashboardAccessedDataSchema,
    [AuditTrailEntryType.supersetDashboardAccessed]: supersetDashboardAccessedDataSchema,
    [AuditTrailEntryType.supersetDashboardExported]: supersetDashboardExportedDataSchema,
    [AuditTrailEntryType.adminRunAutoMergeJob]: emptyAdminDataSchema,
};
//# sourceMappingURL=admin.js.map